import React, { useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'

// Components
import { MCPChatWidget } from './components'
import { LoadingSpinner } from './components/atoms'
import Sidebar from './components/Sidebar'
import DevToolbar from './components/DevToolbar'
import sidebarStyles from './components/Sidebar/Sidebar.module.scss'

// Views
import HomePageView from './views/user/Home'
import AnalyticsDashboardPage from './views/user/Dashboard'
import AnalyticsMetricsPage from './views/user/Metrics'
import CampaignDashboardPage from './views/user/CampaignDashboard'
import AdminCampaignListPage from './views/admin/AdminCampaignList'
import AdminCampaignManagement from './views/admin/AdminCampaignManagement'
import AdminCallListPage from './views/admin/AdminCallList'
import AvailabilityPage from './views/user/Availability'
import GlossaryPage from './views/user/Glossary'
import SettingsPage from './views/settings/Settings'
import NotFoundPage from './views/NotFound'
import ErrorPage from './views/Error'
import SOPPage from './views/user/CallSOP'
import AdminQASummary from './views/admin/AdminQASummary'
import AdminUsageOverview from './views/admin/AdminUsageOverview'
import SettingsFive9Sync from './views/settings/SettingsFive9Sync'
import BillingPage from './views/user/Billing'
import AdminBillingManagement from './views/admin/AdminBillingManagement'
import AdminBillingPlanManagement from './views/admin/AdminBillingPlanManagement'
import AdminBillingClientView from './views/admin/AdminBillingClientView'
import AdminBillingLogs from './views/admin/AdminBillingLogs'
import AdminAuthMigration from './views/admin/AdminAuthMigration'

// Context
import { ThemeProvider } from './contexts/ThemeContext'

// Services
import { getMonthStart, getCurrentDate } from './services/date'
import { apiEndpoints } from './consts'
import { authenticatedFetch } from './services/api/apiClient'

import { isDevelopment } from './devAuth'
import useAuthz from './hooks/useAuthz'
import logger from './utils/logger'

import './App.scss'

import initFontAwesome from './utils/initFontAwesome'
initFontAwesome()

const COLLAPSED_KEY = 'sidebar_collapsed'

const ROUTE_TITLES = {
  '/': 'Impact Overview',
  '/dashboard': 'Dashboard',
  '/campaigns': 'Campaigns',
  '/campaigns/manage': 'Campaign Management',
  '/calls': 'Call Search',
  '/qa-summary': 'QA Summary',
  '/usage-overview': 'Usage Overview',
  '/availability': 'Availability',
  '/glossary': 'Glossary',
  '/settings': 'Settings',
  '/billing': 'Billing',
  '/billing/admin': 'Billing Management',
  '/billing/plans': 'Plan Management',
  '/billing/admin/logs': 'Logs',
  '/auth-migration': 'Auth Migration',
  '/five9-sync': 'Five9 Data Sync',
  '/error': 'Error'
}

const getPageTitle = (pathname) => {
  if (ROUTE_TITLES[pathname]) return ROUTE_TITLES[pathname]
  if (pathname.startsWith('/settings/')) return 'Settings'
  if (pathname.startsWith('/sop/')) return 'Call SOP'
  if (pathname.startsWith('/campaign/')) {
    const name = decodeURIComponent(pathname.split('/campaign/')[1] || '')
    return name ? `${name} - Campaign` : 'Campaign'
  }
  if (pathname.startsWith('/billing/admin/view/')) return 'Client Billing View'
  return 'Page Not Found'
}

const AppLayout = ({ children, isAuthenticated, user, onLogout }) => {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true'
    } catch (e) {
      return false
    }
  })

  // Update document title and scroll to top on route change
  useEffect(() => {
    document.title = `${getPageTitle(location.pathname)} - Veta Virtual`
    window.scrollTo(0, 0)
  }, [location.pathname])

  useEffect(() => {
    const sync = () => {
      try {
        setCollapsed(localStorage.getItem(COLLAPSED_KEY) === 'true')
      } catch (e) {
        /* noop */
      }
    }
    const onStorage = (event) => {
      if (event.key !== null && event.key !== COLLAPSED_KEY) return
      sync()
    }
    window.addEventListener('storage', onStorage)
    window.addEventListener('sidebar-collapsed-change', sync)
    return () => {
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('sidebar-collapsed-change', sync)
    }
  }, [])

  const validRoutes = [
    '/',
    '/dashboard',
    '/campaigns',
    '/campaigns/manage',
    '/calls',
    '/availability',
    '/glossary',
    '/error',
    '/settings',
    '/billing',
    '/billing/admin',
    '/billing/plans',
    '/billing/admin/logs',
    '/auth-migration',
    '/five9-sync',
    '/qa-summary',
    '/usage-overview'
  ]

  const isValidRoute = (pathname) => {
    if (validRoutes.includes(pathname)) return true
    if (/^\/sop\/[^/]+$/.test(pathname)) return true
    if (/^\/campaign\/[^/]+$/.test(pathname)) return true
    if (/^\/settings\/[^/]+$/.test(pathname)) return true
    if (/^\/billing\/admin\/view\/\d+$/.test(pathname)) return true
    return false
  }

  const shouldHideSidebar = location.pathname === '/error' || !isValidRoute(location.pathname)
  const showSidebar = isAuthenticated && !shouldHideSidebar
  const isFullWidth = ['/qa-summary'].includes(location.pathname)

  const contentClass = [
    sidebarStyles.contentArea,
    !showSidebar ? sidebarStyles.contentNoSidebar : '',
    collapsed && showSidebar ? sidebarStyles.contentCollapsed : ''
  ]
    .filter(Boolean)
    .join(' ')

  const innerClass = shouldHideSidebar
    ? sidebarStyles.contentInnerHidden
    : isFullWidth
      ? sidebarStyles.contentInnerFullWidth
      : sidebarStyles.contentInner

  return (
    <>
      <a href='#main-content' className='skip-to-content'>
        Skip to main content
      </a>
      {showSidebar && <Sidebar isAuthenticated={isAuthenticated} user={user} onLogout={onLogout} />}
      <main id='main-content' className={contentClass}>
        <div className={innerClass}>{children}</div>
      </main>
    </>
  )
}

AppLayout.propTypes = {
  children: PropTypes.node,
  isAuthenticated: PropTypes.bool,
  user: PropTypes.object,
  onLogout: PropTypes.func
}

// Auth gate — renders children when allowed, otherwise shows home/login page
const ProtectedRoute = ({ requireAdmin = false, requireDev = false, children }) => {
  const { isAuthenticated, isAdmin } = useAuthz()
  const isAllowed = isAuthenticated && (!requireAdmin || isAdmin) && (!requireDev || isDevelopment)
  return isAllowed ? children : <HomePageView />
}
ProtectedRoute.propTypes = {
  requireAdmin: PropTypes.bool,
  requireDev: PropTypes.bool,
  children: PropTypes.node
}

const App = () => {
  const { user, isAuthenticated, isLoading, error, organization, isAdmin, logout } = useAuthz()

  useEffect(() => {
    if (isAuthenticated && user) {
      const userDetails = {
        sub: user?.sub,
        email: user.email,
        name: user.name,
        picture: user.picture,
        nickname: user.nickname,
        email_verified: user.email_verified,
        given_name: user.given_name,
        family_name: user.family_name,
        locale: user.locale
      }

      authenticatedFetch(apiEndpoints.user, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userDetails)
      })
        .then((response) => {
          if (!response.ok) {
            logger.error('Failed to upsert user details')
          }
        })
        .catch((err) => {
          logger.error('Error upserting user details:', err)
        })
    }
  }, [isAuthenticated, user])

  const now = getCurrentDate()
  const monthStart = getMonthStart()

  if (error) {
    return <div>Oops... {error.message}</div>
  }

  if (isLoading) {
    return <LoadingSpinner fullPage />
  }

  return (
    <ThemeProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppLayout isAuthenticated={isAuthenticated} user={user} onLogout={logout}>
          <Routes>
            <Route
              path='/'
              element={
                <ProtectedRoute>
                  <AnalyticsMetricsPage company={organization} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/dashboard'
              element={
                <ProtectedRoute>
                  <AnalyticsDashboardPage
                    isAuthenticated={isAuthenticated}
                    userId={user?.sub}
                    company={organization}
                    startDate={monthStart}
                    endDate={now}
                    campaignFilter={organization}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path='/campaigns'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCampaignListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/campaigns/manage'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCampaignManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path='/calls'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminCallListPage userId={user?.sub} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/qa-summary'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminQASummary />
                </ProtectedRoute>
              }
            />
            <Route
              path='/usage-overview'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminUsageOverview />
                </ProtectedRoute>
              }
            />
            <Route
              path='/availability'
              element={
                <ProtectedRoute requireAdmin requireDev>
                  <AvailabilityPage isAuthenticated={isAuthenticated} campaign={organization} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/campaign/:campaignName'
              element={
                <ProtectedRoute requireAdmin>
                  <CampaignDashboardPage
                    isAuthenticated={isAuthenticated}
                    userId={user?.sub}
                    company={organization}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path='/sop/:campaignName'
              element={
                <ProtectedRoute requireDev>
                  <SOPPage user={user} isAuthenticated={isAuthenticated} isLoading={isLoading} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/glossary'
              element={
                <ProtectedRoute>
                  <GlossaryPage isAuthenticated={isAuthenticated} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/settings'
              element={
                <ProtectedRoute>
                  <SettingsPage userID={user?.sub} userOrganization={organization} user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/settings/:tab'
              element={
                <ProtectedRoute>
                  <SettingsPage userID={user?.sub} userOrganization={organization} user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/billing'
              element={
                <ProtectedRoute requireDev>
                  <BillingPage user={user} userOrganization={organization} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/billing/admin'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminBillingManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path='/billing/admin/view/:clientAccountId'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminBillingClientView user={user} />
                </ProtectedRoute>
              }
            />
            <Route
              path='/billing/plans'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminBillingPlanManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path='/billing/admin/logs'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminBillingLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path='/auth-migration'
              element={
                <ProtectedRoute requireAdmin>
                  <AdminAuthMigration />
                </ProtectedRoute>
              }
            />
            <Route
              path='/five9-sync'
              element={
                <ProtectedRoute requireAdmin>
                  <SettingsFive9Sync />
                </ProtectedRoute>
              }
            />
            <Route path='/error' element={<ErrorPage />} />
            <Route path='*' element={<NotFoundPage />} />
          </Routes>
        </AppLayout>
        {isAuthenticated && isAdmin && <MCPChatWidget />}
      </BrowserRouter>
      <DevToolbar />
    </ThemeProvider>
  )
}

export default App
